import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  BarChart3, Download, FileSpreadsheet, FileText,
  Calendar, Wrench, AlertTriangle, CheckCircle, ChevronRight,
  TrendingUp, Flag
} from 'lucide-react';

const PERIOD_LABELS = {
  today: 'Astazi',
  week: 'Ultimele 7 zile',
  month: 'Ultima luna',
  quarter: 'Ultimul trimestru',
  semester: 'Ultimele 6 luni',
  year: 'Ultimul an',
  custom: 'Perioada personalizata',
};

const PRIORITY_COLORS = {
  CRITIC: '#dc2626',
  URGENT: '#f97316',
  RIDICAT: '#f59e0b',
  MEDIU: '#3b82f6',
  SCAZUT: '#10b981',
};

const GRANULARITY_LABELS = {
  day: 'pe zile',
  week: 'pe saptamani',
  month: 'pe luni',
};

const formatTickDate = (iso, granularity) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (granularity === 'month') {
    return d.toLocaleDateString('ro-RO', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' });
};

const isoToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const PortalReportsPage = () => {
  const { user } = useAuth();
  const { selectedPlant, selectedLocation } = useData();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [customStart, setCustomStart] = useState(isoDaysAgo(30));
  const [customEnd, setCustomEnd] = useState(isoToday());
  const [stats, setStats] = useState({
    ticketsCreated: 0,
    ticketsResolved: 0,
    equipmentIssues: 0,
    start: null,
    end: null,
  });
  const [topIssues, setTopIssues] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [granularity, setGranularity] = useState('day');
  const [priorityBreakdown, setPriorityBreakdown] = useState([]);

  const queryParams = useMemo(() => {
    const p = { period };
    if (selectedPlant && selectedPlant !== 'all') p.plant_id = selectedPlant;
    if (selectedLocation && selectedLocation !== 'all') p.location_id = selectedLocation;
    if (user?.plant_access?.length > 0) p.plant_access = user.plant_access.join(',');
    if (user?.location_access?.length > 0) p.location_access = user.location_access.join(',');
    if (period === 'custom') {
      p.start_date = customStart;
      p.end_date = customEnd;
    }
    return p;
  }, [period, customStart, customEnd, selectedPlant, selectedLocation, user]);

  useEffect(() => {
    // For custom period, only fetch when both dates are set
    if (period === 'custom' && (!customStart || !customEnd)) return;
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/tickets/stats', { params: queryParams });
      const data = res.data || {};
      setStats({
        ticketsCreated: data.ticketsCreated || 0,
        ticketsResolved: data.ticketsResolved || 0,
        equipmentIssues: data.equipmentIssues || 0,
        start: data.start || null,
        end: data.end || null,
      });
      setTopIssues(data.topIssues || []);
      setTimeline(data.timeline || []);
      setGranularity(data.timelineGranularity || 'day');
      setPriorityBreakdown(data.priorityBreakdown || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Eroare la incarcarea rapoartelor');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    try {
      const totalPri = priorityBreakdown.reduce((a, b) => a + (b.count || 0), 0);
      const meta = [
        ['Raport Statistici Tichete - Fereastra ADF'],
        [`Perioada: ${PERIOD_LABELS[period]} (${stats.start || '-'} → ${stats.end || '-'})`],
        [`Generat: ${new Date().toLocaleString('ro-RO')}`],
        [],
        ['Tichete Create', 'Tichete Rezolvate', 'Echipamente cu Probleme'],
        [stats.ticketsCreated, stats.ticketsResolved, stats.equipmentIssues],
        [],
        ['Top Echipamente cu Probleme'],
        ['#', 'Echipament', 'Cod', 'Nr. Tichete'],
        ...topIssues.map((it, idx) => [idx + 1, it.name || 'N/A', it.code || '-', it.count || 0]),
        [],
        ['Distributie dupa Prioritate'],
        ['Prioritate', 'Nr. Tichete', '%'],
        ...priorityBreakdown.map((p) => [
          p.label,
          p.count || 0,
          totalPri ? `${((p.count / totalPri) * 100).toFixed(1)}%` : '0.0%',
        ]),
        [],
        [`Evolutie in timp (${GRANULARITY_LABELS[granularity] || 'pe zile'})`],
        ['Data', 'Tichete Create', 'Tichete Rezolvate'],
        ...timeline.map((t) => [t.date, t.created || 0, t.resolved || 0]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(meta);
      ws['!cols'] = [{ wch: 22 }, { wch: 45 }, { wch: 18 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Statistici');
      const fname = `raport_statistici_${stats.start || 'now'}_${stats.end || 'now'}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success('Raport Excel exportat cu succes');
    } catch (e) {
      toast.error('Eroare la exportul Excel');
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get('/tickets/stats/pdf', {
        params: queryParams,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raport_statistici_${stats.start || 'now'}_${stats.end || 'now'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Raport PDF exportat cu succes');
    } catch (e) {
      toast.error('Eroare la exportul PDF');
    }
  };

  const handleEquipmentClick = (equipmentId) => {
    if (!equipmentId) {
      toast.info('Echipament fara identificator - nu se poate naviga');
      return;
    }
    navigate(`/portal/tickets?equipment_id=${equipmentId}`);
  };

  const chartTimeline = useMemo(
    () => timeline.map((t) => ({ ...t, label: formatTickDate(t.date, granularity) })),
    [timeline, granularity]
  );

  const totalPriority = priorityBreakdown.reduce((a, b) => a + (b.count || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-20 lg:pb-6" data-testid="portal-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Rapoarte Sectie</h1>
          <p className="text-slate-500">
            Statistici si analize
            {stats.start && stats.end && (
              <span className="ml-2 text-xs text-slate-400">• {stats.start} → {stats.end}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48" data-testid="period-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Astazi</SelectItem>
              <SelectItem value="week">Ultimele 7 zile</SelectItem>
              <SelectItem value="month">Ultima luna</SelectItem>
              <SelectItem value="quarter">Ultimul trimestru</SelectItem>
              <SelectItem value="semester">Ultimele 6 luni</SelectItem>
              <SelectItem value="year">Ultimul an</SelectItem>
              <SelectItem value="custom">Perioada personalizata</SelectItem>
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPdf} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Custom date range inputs */}
      {period === 'custom' && (
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6 bg-blue-50 border border-blue-100 rounded-xl p-4" data-testid="custom-range-row">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">De la</label>
            <Input
              type="date"
              value={customStart}
              max={customEnd}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-48"
              data-testid="custom-start-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Pana la</label>
            <Input
              type="date"
              value={customEnd}
              min={customStart}
              max={isoToday()}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-48"
              data-testid="custom-end-input"
            />
          </div>
          <p className="text-xs text-slate-500 sm:ml-auto">Selecteaza intervalul dorit (intervalul este inclusiv in ambele capete).</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Se incarca...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card data-testid="stat-tickets-created">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Tichete Create</p>
                    <p className="text-3xl lg:text-4xl font-bold text-slate-900">{stats.ticketsCreated}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-tickets-resolved">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Tichete Rezolvate</p>
                    <p className="text-3xl lg:text-4xl font-bold text-green-600">{stats.ticketsResolved}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-equipment-issues">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Echipamente cu Probleme</p>
                    <p className="text-3xl lg:text-4xl font-bold text-orange-600">{stats.equipmentIssues}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Timeline + Priority breakdown row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Timeline chart (occupies 2 cols on lg) */}
            <Card className="lg:col-span-2" data-testid="timeline-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Evolutie in timp
                </CardTitle>
                <CardDescription>
                  Tichete create vs rezolvate — {GRANULARITY_LABELS[granularity] || 'pe zile'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chartTimeline.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
                    Nicio data disponibila pentru perioada selectata
                  </div>
                ) : (
                  <div className="w-full h-64" data-testid="timeline-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartTimeline} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colCreated" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colResolved" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                          width={32}
                        />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                          labelStyle={{ fontWeight: 600, color: '#0f172a' }}
                          formatter={(v, name) => [v, name === 'created' ? 'Create' : 'Rezolvate']}
                        />
                        <Legend
                          iconType="circle"
                          wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                          formatter={(value) => (value === 'created' ? 'Create' : 'Rezolvate')}
                        />
                        <Area type="monotone" dataKey="created" stroke="#3b82f6" fill="url(#colCreated)" strokeWidth={2} />
                        <Area type="monotone" dataKey="resolved" stroke="#10b981" fill="url(#colResolved)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Priority breakdown */}
            <Card data-testid="priority-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-600" />
                  Distributie prioritati
                </CardTitle>
                <CardDescription>
                  Total: <span className="font-semibold text-slate-700">{totalPriority}</span> tichete
                </CardDescription>
              </CardHeader>
              <CardContent>
                {totalPriority === 0 ? (
                  <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
                    Fara tichete in perioada
                  </div>
                ) : (
                  <>
                    <div className="w-full h-40" data-testid="priority-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={priorityBreakdown} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} width={24} />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                            formatter={(v) => [v, 'Tichete']}
                          />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {priorityBreakdown.map((p) => (
                              <Cell key={p.label} fill={PRIORITY_COLORS[p.label] || '#64748b'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-3 space-y-1" data-testid="priority-list">
                      {priorityBreakdown.map((p) => {
                        const pct = totalPriority ? ((p.count / totalPriority) * 100).toFixed(1) : '0.0';
                        return (
                          <div key={p.label} className="flex items-center gap-2 text-xs">
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ background: PRIORITY_COLORS[p.label] || '#64748b' }}
                            />
                            <span className="font-medium text-slate-700 flex-1">{p.label}</span>
                            <span className="text-slate-500 tabular-nums">{p.count}</span>
                            <span className="text-slate-400 w-12 text-right tabular-nums">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Issues */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Top Echipamente cu Probleme
              </CardTitle>
              <CardDescription>Cele mai frecvente probleme raportate — click pe echipament pentru a vedea tichetele</CardDescription>
            </CardHeader>
            <CardContent>
              {topIssues.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p>Nicio problema frecventa in perioada selectata</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="top-issues-list">
                  {topIssues.map((item, index) => (
                    <button
                      key={item.equipment_id || item.name}
                      type="button"
                      onClick={() => handleEquipmentClick(item.equipment_id)}
                      data-testid={`top-issue-${index}`}
                      className="w-full flex items-center gap-4 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200 text-left group"
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        index === 0 ? 'bg-red-100 text-red-600' :
                        index === 1 ? 'bg-orange-100 text-orange-600' :
                        index === 2 ? 'bg-yellow-100 text-yellow-600' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate group-hover:text-blue-700">{item.name}</p>
                        {item.code && <p className="text-xs text-slate-400 truncate">Cod: {item.code}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min(100, (item.count / topIssues[0].count) * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-600 w-12 text-right">
                          {item.count}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PortalReportsPage;
